import { mediaService } from '@/services/api'
import { groupByArrayOfObject } from '@/common/arrays'
import { MediaStatus, MediaType } from '@/constants'

const initialStates = {
    status: 'LOADING',
    mediaList: [],
    pagination: {
        no_paginate: false,
        per_page: 20,
        current_page: 1,
        last_page: 1,
    },
    params: {
        page: 1
    }
}

const mutations = {
    loading(state) {
        state.status = 'LOADING'
    },
    setMediaList(state, media) {
        state.status = 'SET_LIST'
        state.mediaList = media
    },
    setPagination(state, pagination) {
        state.status = 'SET_PAGINATION'
        state.pagination = Object.assign({}, state.pagination, pagination)
    },
    setParams(state, params) {
        state.status = 'SET_PARAMS'
        state.params = Object.assign({}, state.params, params)
    },
    resetParams(state) {
        state.status = 'RESET_PARAMS'
        state.params = {
            page: 1
        }
    },
    createMedium(state, medium) {
        state.status = 'CREATE'
        state.mediaList = Object.assign({[medium.id]: medium}, state.mediaList)
    },
    updateMedium(state, medium) {
        state.status = 'UPDATE'
        state.mediaList[medium.id] = Object.assign({}, state.mediaList[medium.id], medium)
    },
    deleteMedium(state, id) {
        state.status = 'DELETE_FROM_LIST'
        delete state.mediaList[id]
    },
    error(state) {
        state.status = 'ERROR'
    },
}

const getters = {
    status: state   => state.status,
    params: state => state.params,
    media: state => state.mediaList,
    pagination: state => state.pagination,
    mediumById: state => id => state.mediaList[id],
    mediaCount: state => Object.keys(state.mediaList).length,
    selectableMedia: (state, getters) => {
        return Object.keys(getters.media).map((id) => {
            return {
                value: id,
                label: getters.media[id].name,
            }
        })
    },
}

const actions = {
    setParamsAndFetch({dispatch}, params) {
        dispatch('setParams', params)
        dispatch('fetch')
    },
    setParams({commit}, params) {
        commit('setParams', params)
    },
    resetParams({commit}) {
        commit('resetParams')
    },
    fetch({commit, state}, params) {
        params = Object.assign({}, state.params, params)

        commit('loading')

        return new Promise((resolve, reject) => {
            mediaService.fetch(params)
                .then(response => {
                    const result = response.data
                    const media = groupByArrayOfObject(result.data, 'id')

                    commit('setMediaList', media)
                    commit('setPagination', {
                        per_page: result.per_page,
                        current_page: result.current_page,
                        last_page: result.last_page,
                    })
                    commit('setParams', {
                        page: result.current_page,
                    })
                    resolve(result)
                })
                .catch(error => {
                    commit('error')
                    reject(error.response ? error.response.data : error.response)
                })
        })
    },
    // Used in AD Manager
    fetchActiveSites(module, params = {}) {
        params = Object.assign({}, params, {
            is_verified: MediaStatus.APPROVED,
            is_certified: MediaStatus.APPROVED,
            type: MediaType.WEBSITE
        })

        return actions.fetch(module, params)
    },
    create({commit}, params) {
        commit('loading')

        return new Promise((resolve, reject) => {
            mediaService.create(params)
                .then(response => {
                    const result = response.data

                    commit('createMedium', result.data)
                    resolve(result)
                })
                .catch(error => {
                    commit('error')
                    reject(error.response ? error.response.data : error.response)
                })
        })
    },
    edit({commit}, params) {
        commit('loading')

        return new Promise((resolve, reject) => {
            mediaService.edit(params, params.id)
                .then(() => {
                    commit('updateMedium', params)
                    resolve()
                })
                .catch(error => {
                    commit('error')
                    reject(error.response ? error.response.data : error.response)
                })
        })
    },
    changeStatus({commit}, params) {
        commit('loading')

        return new Promise((resolve, reject) => {
            mediaService.changeStatus(params.id, params)
                .then(() => {
                    commit('updateMedium', params)
                    resolve()
                })
                .catch(error => {
                    commit('error')
                    reject(error.response ? error.response.data : error.response)
                })
        })
    },
    changeLevel({commit}, params) {
        commit('loading')

        return new Promise((resolve, reject) => {
            mediaService.changeLevel(params.id, params)
                .then(() => {
                    commit('updateMedium', params)
                    resolve()
                })
                .catch(error => {
                    commit('error')
                    reject(error.response ? error.response.data : error.response)
                })
        })
    },
    delete({commit}, hashid) {
        commit('loading')

        return new Promise((resolve, reject) => {
            mediaService.delete(hashid)
                .then(() => {
                    commit('deleteMedium', hashid)
                    resolve()
                })
                .catch(error => {
                    commit('error')
                    reject(error.response ? error.response.data : error.response)
                })
        })
    },
}

export default {
    namespaced: true,
    state: initialStates,
    mutations: mutations,
    getters: getters,
    actions: actions,
}